// Packages
import { createApp, defineAsyncComponent } from 'vue';

// Modules
import getMedia from '@helpers/breakpoints';

// Vue components
import HeaderSearch from '@vue-components/HeaderSearch.vue';

const isDesktopMedia = getMedia('md');

export default function init(): void {
  const header = document.querySelector<HTMLElement>('#js-header')!;

  // Create Vue instance for header
  const vueInstance = createApp({
    name: 'HeaderInstance',
    compilerOptions: {
      delimiters: ['${', '}'],
    },
    components: {
      HeaderSearch,
      SearchItemCard: defineAsyncComponent(
        () => import('@vue-components/cards/SearchItemCard.vue'),
      ),
    },
    data() {
      return {
        isDesktop: isDesktopMedia.matches,
        isMobileMenuEnabled: false,
        openDropdowns: [],
        lastScrollPosition: 0,
        isHidden: false,
      };
    },
    watch: {
      isHidden(isHidden: boolean) {
        const stickyHeader = header.querySelector('.js-sticky-header');

        if (stickyHeader) {
          stickyHeader.classList.add('transition-transform', 'duration-300');

          if (isHidden) {
            stickyHeader.classList.add('-translate-y-[200%]');
          } else {
            stickyHeader.classList.remove('-translate-y-[200%]');
          }
        }
      },
      isDesktop(newIsDesktop: boolean) {
        // When switching to desktop, reset mobile menu state
        if (newIsDesktop) {
          this.isMobileMenuEnabled = false;
        }
      },
      isMobileMenuEnabled(isMobileMenuEnabled: boolean) {
        if (isMobileMenuEnabled) {
          this.isHidden = false;
        }
        document.body.classList.toggle('overflow-hidden', isMobileMenuEnabled);
      },
    },
    created() {
      // Make isMobile responsive
      if (isDesktopMedia.addEventListener) {
        isDesktopMedia.addEventListener('change', this.isDesktopMediaListener);
      } else {
        isDesktopMedia.addListener(this.isDesktopMediaListener);
      }

      window.addEventListener('scroll', this.handleScroll);
    },
    beforeUnmount() {
      if (isDesktopMedia.removeEventListener) {
        isDesktopMedia.removeEventListener('change', this.isDesktopMediaListener);
      } else {
        isDesktopMedia.removeListener(this.isDesktopMediaListener);
      }

      window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
      isDesktopMediaListener(media: MediaQueryListEvent) {
        this.isDesktop = media.matches;
      },
      handleScroll() {
        if (this.isMobileMenuEnabled) return;

        const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        if (currentScrollPosition < 0) {
          return;
        }

        if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 60) {
          return;
        }

        this.isHidden = currentScrollPosition > this.lastScrollPosition;
        this.lastScrollPosition = currentScrollPosition;
      },
      toggleDropdown(id: number | string, force?: boolean) {
        const open = force === undefined ? !this.openDropdowns.includes(id) : force;

        if (open) {
          this.openDropdowns.push(id);
        } else {
          this.openDropdowns = this.openDropdowns.filter(
            (dropdown: number | string) => dropdown !== id,
          );
        }
      },
      isDropdownOpen(id: number | string) {
        return this.openDropdowns.includes(id);
      },
    },
  });

  // Mount the app
  vueInstance.mount(header);
}
